import React, { useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { Shuffle, GpsFixed, PlusOne, Sd, Hd, TimesOneMobiledata, AllInclusive, Queue } from "@mui/icons-material";
import { ReactComponent as FlyIcon } from './fly.svg'
import { useColorMode } from "../ColorModeContext";
import { useComfy } from "../comfy/ComfyProvider";




interface OutputPanelProps {
  selectedCheckpoint: string;
  setSelectedCheckpoint: (value: string) => void;
  checkpoints: string[][]; 
  cfg: number;
  setCfg: (value: number) => void;
  cnetEdges: number;
  setCnetEdges: (value: number) => void;
  cnetVolume: number;
  setCnetVolume: (value: number) => void;
  steps: number;
  setSteps: (value: number) => void;
  seed: number;
  setSeed: (value: number) => void;
  seedMode: string;
  handleSeedModeChange: (_event: React.MouseEvent<HTMLElement>, newMode: string | null) => void;
  resolution: string;
  handleResolutionChange: (_event: React.MouseEvent<HTMLElement>, newResolution: string | null) => void;
  generate: () => void;
  generationMode: boolean; 
  isGenerating: boolean;
  setGenerationMode: React.Dispatch<React.SetStateAction<boolean>>
  stopQueue: () => void;
}

const OutputPanel: React.FC<OutputPanelProps> = ({
  selectedCheckpoint,
  setSelectedCheckpoint,
  checkpoints,
  cfg,
  setCfg,
  steps,
  setSteps,
  cnetVolume,
  setCnetVolume,
  cnetEdges,
  setCnetEdges,
  seed,
  setSeed,
  seedMode,
  handleSeedModeChange,
  resolution,
  handleResolutionChange,
  generate,
  generationMode, 
  setGenerationMode,
  isGenerating,
  stopQueue,
}) => {

  const {colorMode} = useColorMode();

  const {jobStatus} = useComfy();

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;

    if (!generationMode) {  // If generationMode is false (endless)
      intervalId = setInterval(generate, 6500); // Calls generate every 15 seconds
    }


    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [generationMode, generate]); // dependency array ensures it runs on mode change


  return (
    <div style={{marginTop:'10px'}}>
      {/* Title */}
      <div className="Title">
        <h1 className={colorMode?"TopNumberDark":"TopNumberLight"}>3</h1>
        <h1 className="Title">Output</h1>
      </div>
      <h4 className="SubTitle">Select the output settings</h4>

      {/* Select Model 
      <h5 className="SubTitle">Select Model</h5>
      <Select
        value={selectedCheckpoint}
        onChange={(e: SelectChangeEvent<string>) => setSelectedCheckpoint(e.target.value)}
        style={{ width: "100%", maxHeight: "40px", marginBottom: "10px" }}
      >
        {checkpoints?.length > 0 &&
          checkpoints[0].map((option, index) => (
            <MenuItem key={index} value={option}>
              {option}
            </MenuItem>
          ))}
      </Select>*/}

      <Box sx={{ display: "flex", flexDirection: "row" }}>
        {/* CFG Slider */}
        <Box sx={{ flex: "1", pr: "10px" }}>
          <h5 className="SubTitle">Imagination (cfg) = {cfg}</h5>
          <Slider value={cfg} min={1} max={5} step={0.25} onChange={(_, value) => setCfg(value as number)} />
        </Box>

        {/* Steps Slider */}
        <Box sx={{ flex: "1", pl: "10px" }}>
          <h5 className="SubTitle">Iterations (steps) = {steps}</h5>
          <Slider value={steps} min={1} max={20} step={1} onChange={(_, value) => setSteps(value as number)} />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "row" }}>
        {/* CFG Slider */}
        <Box sx={{ flex: "1", pr: "10px" }}>
          <h5 className="SubTitle">Volume (cnet) = {cnetVolume}</h5>
          <Slider value={cnetVolume} min={0.05} max={1} step={0.05} onChange={(_, value) => setCnetVolume(value as number)} />
        </Box>

        {/* Steps Slider */}
        <Box sx={{ flex: "1", pl: "10px" }}>
          <h5 className="SubTitle">Edge (cnet) = {cnetEdges}</h5>
          <Slider value={cnetEdges} min={0.05} max={1} step={0.05} onChange={(_, value) => setCnetEdges(value as number)} />
        </Box>
      </Box>

      {/* Seed Selection */}
      <h5 style={{ margin: "0" }}>Seed</h5>
      <TextField
        variant="standard"
        value={seed}
        type="number"
        onChange={(e) => setSeed(Number(e.target.value))}
        rows={2}
        style={{ width: "100%", marginBottom: "10px" }}
      />

      {/* Seed Mode Toggle */}
      <Stack spacing={1}>
        <ToggleButtonGroup
          value={seedMode}
          exclusive
          onChange={handleSeedModeChange}
          style={{ display: "flex", justifyContent: "left", marginBottom: "10px", overflow: "auto" }}
        >
          <ToggleButton value="random" aria-label="seedRandom" style={{ width: "100%" }}>
            <Shuffle sx={{ mr: "20px" }} />
            Random
          </ToggleButton>
          <div className="separatorVertical" />
          <ToggleButton value="fixed" aria-label="seedFixed" style={{ width: "100%" }}>
            <GpsFixed sx={{ mr: "20px" }} />
            Fixed
          </ToggleButton>
          <div className="separatorVertical" />
          <ToggleButton value="incremental" aria-label="seedIncremental" style={{ width: "100%" }}>
            <PlusOne sx={{ mr: "20px" }} />
            Incremental
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Quality Selection */}
      <Stack spacing={2}>
        <ToggleButtonGroup
          value={resolution}
          exclusive
          onChange={handleResolutionChange}
          style={{ display: "flex", justifyContent: "left", overflow: "auto" }}
        >
          <ToggleButton value="SD" aria-label="sd" style={{ width: "100%" }}>
            <Sd sx={{ mr: "20px" }} />
            Standard Definition
          </ToggleButton>
          <div className="separatorVertical" />
          <ToggleButton value="HD" aria-label="hd" style={{ width: "100%" }}>
            <Hd sx={{ mr: "20px" }} />
            High Definition
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Generate Button */}
      <Box sx={{mt:'10px', mb:'15px'}}>
      <Stack direction={'row'}  alignItems="center" >
      {/*<Button 
      variant="contained"
      sx={{height:'5vh', m:0, p:'10px', color:'white',backgroundColor:"#3680d330", '&:hover': {
        backgroundColor: '#215899'},}}
        onClick={() => {
          setGenerationMode(prev => !prev); //chanes teh generation mode to endless
          stopQueue(); //and starts a new queue
        }}>
      {generationMode ? 
      <Tooltip title='Sets generation mode to endless' arrow>
        <AllInclusive />
      </Tooltip> 
      :
      <Tooltip title='Sets generation mode to generate on click' arrow>
        <TimesOneMobiledata />
      </Tooltip>}
      </Button> */}

      {/*<Button 
      variant="contained"
      sx={{height:'5vh', m:0, ml:'5px', p:'10px', color:'white', '&:hover': {
        backgroundColor: '#215899'},}}
      onClick={generate}>
      
      <Tooltip title='Adds prompt to the queue' arrow>
        <Queue />
      </Tooltip>
      </Button>*/}

      
      


      {jobStatus === "IN_QUEUE" || jobStatus==="IN_PROGRESS" ? ( 
      <Tooltip title='Stops queue and sends new prompt' arrow>
      <Button
          variant= "contained"
          disabled
          onClick={stopQueue}
          sx={{
            width: '100%',
            p: '10px',
            alignItems: 'center',
            height: '5vh',
            ml:'0px',
          }}
           // Disable button if in queue or generating
        >
          <h2 style={{ margin: '0', padding: '0', }}>Fly.....</h2><FlyIcon style={{ fill:'grey' ,height:"2.5vh", marginLeft:'0px'}}/>
        </Button>
        </Tooltip>) : (

<Tooltip title='Stops queue and sends new prompt' arrow>
<Button
    variant= "contained"
    
    onClick={stopQueue}
    sx={{
      width: '100%',
      p: '10px',
      alignItems: 'center',
      height: '5vh',
      ml:'0px',
    }}
     
  >
    <h2 style={{ margin: '0', padding: '0', }}>Fly.....</h2><FlyIcon style={{ fill:'white' ,height:"2.5vh", marginLeft:'0px'}}/>
  </Button>
  </Tooltip>
        )}
    
      </Stack>
      </Box>

    </div>
  );
};

export default OutputPanel;
